import { Button, Menu, MenuItem } from '@mui/material';
import { ITechnician } from '../../../utils/entities/interface';
import { useNavigate } from 'react-router-dom';
import { RemoveRedEyeOutlined } from '@mui/icons-material';
import { ListTable } from '../../molecules/ListTable';
import { formatTechnicianType } from '../../../utils/helper';
import { useState } from 'react';
import { ToggleTechnicianStatus } from '../../../services/technician';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import useSnackbar from '../../../hooks/useSnackbar';
import { updateToolRequestStatus } from '../../../services/toolRequest';

export const ToolRequestList = ({
  technicians,
  isLoading,
  onUpdate,
  headerCells,
}: {
  technicians: any;
  headerCells: [string, number][];
  isLoading: boolean;
  onUpdate: () => void;
}) => {
  const [openModal, setOpenModal] = useState<boolean>(false);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const navigate = useNavigate();
  const [selectedTechnician, setSelectedTechnician] =
    useState<ITechnician | null>(null);

  // State for status menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  const [loadingAction, setLoadingAction] = useState<boolean>(false);
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setOpenModal(false);
    setSelectedTechnician(null);
  };

  const handleOpenModal = (technician: ITechnician) => {
    setSelectedTechnician(technician);
    setOpenModal(true);
  };

  const handleConfirmToggle = async () => {
    if (!selectedTechnician) return;
    await ToggleTechnicianStatus(selectedTechnician._id);
    onUpdate();
    handleCloseModal();
  };

  const handleCloseModal = () => {
    setSelectedTechnician(null);
    setOpenModal(false);
  };

  // Handle status menu open
  const handleStatusClick = (
    event: React.MouseEvent<HTMLElement>,
    request: any,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedRequest(request);
  };

  // Handle status menu close
  const handleStatusClose = () => {
    setAnchorEl(null);
    setSelectedRequest(null);
  };

  // API call for updating status
  const updateRequestStatus = async (
    status: 'APPROVED' | 'DENIED' | 'ASSIGNED',
  ) => {
    if (!selectedRequest) return;

    setLoadingAction(true);
    try {
      await updateToolRequestStatus(selectedRequest._id, status);
      showSnackbar(`Request ${status.toLowerCase()} successfully!`, 'success');
      onUpdate();
    } catch (error: unknown) {
      console.error('Error updating request status:', error);
      showSnackbar(
        error instanceof Error
          ? error.message
          : `Failed to ${status.toLowerCase()} request`,
        'error',
      );
    } finally {
      setLoadingAction(false);
      handleStatusClose();
    }
  };

  const actions = [
    {
      icon: <RemoveRedEyeOutlined />,
      onClick: (technician: ITechnician) =>
        navigate(`/technicians/${technician._id}`),
    },
  ];

  return (
    <>
      {SnackbarComponent}
      <ListTable
        headerCells={headerCells}
        rows={technicians}
        isLoading={isLoading}
        actions={actions}
        renderCell={(technician: any, colIndex: number) => {
          switch (colIndex) {
            case 0:
              return technician.toolRequestId || 'N/A';
            case 1:
              return technician.tools?.name || 'N/A';
            case 2:
              return formatTechnicianType(technician.type);
            case 3:
              return technician?.technician?.name || 'N/A';
            case 4:
              return technician.reason || 'N/A';
            case 5:
              return technician?.quantity || 'N/A';
            case 6:
              return (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => handleStatusClick(e, technician)}
                  endIcon={<KeyboardArrowDownIcon />}
                  disabled={loadingAction}
                  color={
                    technician.status === 'DENIED'
                      ? 'error'
                      : technician.status === 'REQUESTED'
                        ? 'warning'
                        : technician.status === 'ASSIGNED'
                          ? 'info'
                          : 'success'
                  }
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.8rem',
                    borderRadius: '8px',
                  }}
                >
                  {technician.status === 'DENIED'
                    ? 'Rejected'
                    : technician.status === 'REQUESTED'
                      ? 'Requested'
                      : technician.status === 'ASSIGNED'
                        ? 'Assigned'
                        : 'Approved'}
                </Button>
              );
            default:
              return null;
          }
        }}
      />

      {/* Status Menu Dropdown */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleStatusClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 120,
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <MenuItem
          onClick={() => updateRequestStatus('APPROVED')}
          disabled={loadingAction}
          sx={{
            color: '#2e7d32',
            '&:hover': {
              backgroundColor: 'rgba(46, 125, 50, 0.08)',
            },
            '&.Mui-disabled': {
              opacity: 0.5,
            },
          }}
        >
          {loadingAction ? 'Processing...' : 'Approve'}
        </MenuItem>
        <MenuItem
          onClick={() => updateRequestStatus('ASSIGNED')}
          disabled={loadingAction}
          sx={{
            color: '#3b6ea5',
            '&:hover': {
              backgroundColor: 'rgba(46, 125, 50, 0.08)',
            },
            '&.Mui-disabled': {
              opacity: 0.5,
            },
          }}
        >
          {loadingAction ? 'Processing...' : 'Assign'}
        </MenuItem>
        <MenuItem
          onClick={() => updateRequestStatus('DENIED')}
          disabled={loadingAction}
          sx={{
            color: '#7B1FA2',
            '&:hover': {
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
            },
            '&.Mui-disabled': {
              opacity: 0.5,
            },
          }}
        >
          {loadingAction ? 'Processing...' : 'Reject'}
        </MenuItem>
      </Menu>
    </>
  );
};
